import { jsx as _jsx } from "react/jsx-runtime";
import * as React from 'react';
import { cn } from '../lib/cn.js';
/**
 * The default working surface.
 *
 * `Card` is used around 26 times across the app and roughly 38 more on the report page, so its
 * recipe IS the product's texture and any change here is felt everywhere at once. Three
 * adjustments, each small on its own:
 *
 *   - The header padding drops from `p-5` to `px-4 py-3`. At `p-5` a panel wrapping four rows of
 *     data spent more height on its own chrome than on the data, which is what made long pages feel
 *     padded rather than dense.
 *   - `CardTitle` drops from `text-lg` to the `section` step at weight 600. Every panel heading
 *     being 18px is why "Extracted C2 config" and "Suricata alerts: 0" shouted equally loudly.
 *     Panels that genuinely lead a screen opt back up with `text-h3`.
 *   - A hairline hover on interactive cards, so something clickable says so before it is clicked.
 *
 * `Panel` remains the richer primitive when a surface needs to express severity or elevation. This
 * is the neutral default, and the two are visually consistent by construction.
 */
export const Card = React.forwardRef(({ className, interactive, ...rest }, ref) => (_jsx("div", { ref: ref, className: cn('rounded-md border border-border-subtle bg-bg-raised', interactive &&
        'transition-colors duration-fast hover:border-border-default motion-reduce:transition-none', className), ...rest })));
Card.displayName = 'Card';
export const CardHeader = ({ className, ...rest }) => (_jsx("div", { className: cn('flex flex-col gap-1 border-b border-border-subtle px-4 py-3', className), ...rest }));
export const CardTitle = ({ className, children, ...rest }) => (
// `children` is destructured rather than spread so the heading is statically known to have
// content: jsx-a11y/heading-has-content cannot see children arriving through {...rest}.
_jsx("h3", { className: cn('text-section font-semibold text-text-primary', className), ...rest, children: children }));
export const CardDescription = ({ className, ...rest }) => (_jsx("p", { className: cn('text-sm leading-relaxed text-text-secondary', className), ...rest }));
export const CardContent = ({ className, ...rest }) => (_jsx("div", { className: cn('px-4 py-3', className), ...rest }));
//# sourceMappingURL=card.js.map